import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { Button } from "@/components/ui/button";
import {
  DAY_WINDOW_MAX_OFFSET,
  DAY_WINDOW_SIZE,
  getUpcomingDays,
} from "@/lib/api/availability";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useOptimizedAnimation } from "@/utils/animation-utils";
import { WeekDayPicker, type WeekDayPickerProps } from "@/components/v2/home/WeekDayPicker";
import { dayKeyFromDate } from "@/lib/v2/pollAggregates";
import { format } from "date-fns";
import { HorizontalScrollStrip } from "@/components/v2/home/HorizontalScrollStrip";
import { cn } from "@/lib/utils";

type WeekDayCyclerProps = Omit<WeekDayPickerProps, "days" | "slidingWindow"> & {
  windowSize?: number;
  maxOffset?: number;
  animate?: boolean;
  resetKey?: string | number | boolean;
};

const WEEKDAY_SHORT = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

export function WeekDayCycler({
  windowSize = DAY_WINDOW_SIZE,
  maxOffset = DAY_WINDOW_MAX_OFFSET,
  animate = true,
  resetKey,
  hint,
  selected,
  onChange,
  compact,
  showAvailability,
  availability = {},
  disabled,
  pendingDates = [],
  max,
}: WeekDayCyclerProps) {
  const { t } = useTranslation();
  const { isReducedMotion } = useOptimizedAnimation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (resetKey !== undefined) {
      setOffset(0);
    }
  }, [resetKey]);

  const allDays = useMemo(
    () => getUpcomingDays(maxOffset + windowSize).map((date) => ({ date })),
    [maxOffset, windowSize]
  );

  const canPrev = offset > 0;
  const canNext = offset < maxOffset;
  const pending = new Set(pendingDates);

  if (isMobile) {
    return (
      <HorizontalScrollStrip
        hint={
          hint ?? (
            <p className="text-[11px] text-muted-foreground">{t("v2.home.swipeDaysHint")}</p>
          )
        }
        role="group"
        aria-label={t("v2.home.optionalAvailabilityTitle")}
      >
          {allDays.map(({ date }) => {
            const key = dayKeyFromDate(date);
            const isSelected = selected.some((d) => dayKeyFromDate(d) === key);
            const count = availability[key] ?? 0;
            const atMax = max !== undefined && selected.length >= max && !isSelected;
            const isPending = pending.has(key);

            return (
              <button
                key={key}
                type="button"
                disabled={disabled || atMax || isPending}
                aria-pressed={isSelected}
                onClick={() => {
                  if (disabled || isPending) return;
                  if (isSelected) {
                    onChange(selected.filter((d) => dayKeyFromDate(d) !== key));
                    return;
                  }
                  if (max !== undefined && selected.length >= max) return;
                  onChange([...selected, date].sort((a, b) => a.getTime() - b.getTime()));
                }}
                className={cn(
                  "flex min-h-[52px] min-w-[3.25rem] shrink-0 snap-start flex-col items-center justify-center rounded-lg border-2 px-2 py-1.5 transition-transform active:scale-[0.97]",
                  isSelected
                    ? "border-soccer-primary bg-soccer-primary text-white shadow-sm"
                    : atMax
                      ? "cursor-not-allowed border-border bg-card opacity-40"
                      : isPending
                        ? "cursor-wait border-border bg-card opacity-70"
                        : "border-border bg-card"
                )}
              >
                <span
                  className={cn(
                    "text-[10px] font-semibold leading-none",
                    isSelected ? "text-white/90" : "text-muted-foreground"
                  )}
                >
                  {WEEKDAY_SHORT[date.getDay()]}
                </span>
                <span className="mt-0.5 text-sm font-bold tabular-nums leading-none">
                  {format(date, "d")}
                </span>
                {showAvailability && !isSelected && !isPending && count > 0 ? (
                  <span
                    className={cn(
                      "mt-0.5 text-[10px] font-bold tabular-nums",
                      count >= 6 ? "text-soccer-primary" : "text-muted-foreground",
                      isSelected && "text-white/90"
                    )}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
      </HorizontalScrollStrip>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          disabled={!canPrev}
          onClick={() => setOffset((current) => Math.max(0, current - 1))}
          aria-label={t("v2.home.dayWindowPrev")}
        >
          <LuChevronLeft className="h-5 w-5" />
        </Button>

        <div className="min-w-0 flex-1">
          <WeekDayPicker
            selected={selected}
            onChange={onChange}
            max={max}
            showAvailability={showAvailability}
            availability={availability}
            disabled={disabled}
            pendingDates={pendingDates}
            hint={hint}
            compact={compact}
            days={allDays}
            slidingWindow={{
              offset,
              windowSize,
              animate: animate && !isReducedMotion,
            }}
          />
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          disabled={!canNext}
          onClick={() => setOffset((current) => Math.min(maxOffset, current + 1))}
          aria-label={t("v2.home.dayWindowNext")}
        >
          <LuChevronRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
